import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  Alert,
  AlertTitle,
  LinearProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Link,
  TextField,
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import {
  assetsFromVideoId,
  probeAssets,
  uploadVideoFile,
  videoIdFromAssets,
} from './videoUpload';
import { describeError, patchVideo } from '../admin/api';
import { useAdmin, uploadKey, isUploadActive } from '../admin/AdminStore';
import { stalePatch } from '../admin/schema';

// Replacing the file points the record at a brand new api.video asset. The
// subtitle tracks and chapters were derived from the old file's timeline, so
// whether they survive depends on something only the admin knows: did the
// timing change? A re-encode keeps them valid; a recut invalidates all of them.
//
// There are two ways to arrive at that asset. Uploading through the browser is
// the convenient one; pasting the id of a file uploaded straight to api.video
// is the one that still works when the file is 7 GB, when the browser upload
// dies three quarters through, or when a transcode has to be retried from the
// dashboard. Both land in the same place, so the timeline question and the
// stale flags are asked and written once, here, for either route.
const EMPTY = {
  open: false,
  source: 'file',
  file: null,
  videoId: '',
  probe: '',
  checking: false,
  timing: '',
  progress: 0,
  status: '',
  error: '',
  done: false,
};

const PROBE_MESSAGE = {
  ready: ['success', 'Found a playable video with that id.'],
  processing: [
    'warning',
    'That id exists but is not playable yet — it is either still transcoding or the transcode failed. api.video shows the same 202 for both, so check the id in the dashboard: ERROR_PROCESSING there means this file will never play.',
  ],
  missing: ['error', 'No video with that id. Check it against the api.video dashboard.'],
  unknown: ['info', 'Could not reach api.video to check. The id looks well-formed.'],
};

function ReplaceVideoFile({ videoData, videoIdentifier, onUpdateVideoData }) {
  const [state, setState] = useState(EMPTY);
  // The dialog unmounts when the tab changes, taking its progress bar with it,
  // so the run is also registered in the store -- where the Media tab can keep
  // reporting it and nothing can start a second one over the top. See the
  // upload registry in AdminStore.
  const { uploads, beginUpload, updateUpload, clearUpload } = useAdmin();
  const key = uploadKey(videoIdentifier, 'replace');
  const lastPercent = useRef(0);
  const uploadBusy = ['horizontal', 'vertical', 'replace'].some((kind) =>
    isUploadActive(uploads[uploadKey(videoIdentifier, kind)]),
  );

  const subtitleCount = (videoData.subtitles || []).length;
  const chapterCount = (videoData.chapters || []).length;
  const hasDerived = subtitleCount > 0 || chapterCount > 0;
  const currentId = videoIdFromAssets(videoData.assets);

  const set = (patch) => setState((prev) => ({ ...prev, ...patch }));

  const check = async () => {
    const candidate = assetsFromVideoId(state.videoId);
    if (!candidate) {
      set({ probe: '', error: 'That is not an api.video id. Paste the id itself, or any of the video\'s URLs.' });
      return;
    }
    set({ checking: true, probe: '', error: '' });
    const probe = await probeAssets(candidate);
    set({ checking: false, probe });
  };

  const replace = async () => {
    let assets = null;

    if (state.source === 'id') {
      assets = assetsFromVideoId(state.videoId);
      if (!assets) {
        set({ error: 'That is not an api.video id. Paste the id itself, or any of the video\'s URLs.' });
        return;
      }
    } else {
      if (!state.file) return;
      set({ status: 'Uploading…', error: '', progress: 0 });
      lastPercent.current = 0;
      beginUpload(key, {
        identifier: videoIdentifier,
        kind: 'replace',
        filename: state.file.name,
        state: 'uploading',
      });

      // The callback is the early hint; the resolved response is the source of
      // truth. Relying on the callback alone reported a false failure on every
      // upload that finished before api.video had transcoded -- which is all
      // of them.
      let hinted = null;
      try {
        const response = await uploadVideoFile(state.file, {
          // Only on a whole-percent change: every store write re-renders the
          // workspace, and the raw event fires many times a second.
          onProgress: (progress) => {
            set({ progress });
            if (progress === lastPercent.current) return;
            lastPercent.current = progress;
            updateUpload(key, { progress });
          },
          onPlayable: (video) => {
            hinted = video.assets;
          },
        });
        assets = hinted || response?.assets || null;
      } catch (error) {
        const message = `Upload failed: ${error.message}. Nothing was changed.`;
        set({ status: '', error: message });
        updateUpload(key, { state: 'error', message });
        return;
      }

      if (!assets?.mp4) {
        const message = 'The upload finished but api.video returned no playable assets. Nothing was changed.';
        set({ status: '', error: message });
        updateUpload(key, { state: 'error', message });
        return;
      }

      updateUpload(key, { state: 'registering', progress: 100 });
    }

    set({ status: 'Pointing the video record at the new file…', error: '' });

    try {
      // addupdate merges, so only the fields listed here change. When the
      // timeline changed, the derived tracks are marked rather than deleted --
      // four translations are expensive to recreate and the admin may want to
      // regenerate rather than lose them.
      const patch = {
        assets: { hls: assets.hls, mp4: assets.mp4 },
        // The poster is regenerated because the old one is a frame of a file
        // this record no longer points at. The card crop is not -- it is a
        // hand-made 4:5 and only the admin can redo it, so the success view
        // says so instead of leaving a stale crop on the grid unannounced.
        thumbnailUrl: assets.thumbnail,
      };
      // Subtitles and chapters are invalidated together but regenerated
      // separately, so they carry a flag each.
      if (hasDerived) {
        const invalid = state.timing === 'changed';
        Object.assign(patch, stalePatch({ subtitles: invalid, chapters: invalid }));
      }

      onUpdateVideoData(await patchVideo(videoIdentifier, patch));
      set({ status: '', done: true });
      // Nothing left to report: the record is repointed and the dialog says so.
      clearUpload(key);
    } catch (error) {
      // The upload succeeded, so a failure here leaves an api.video asset that
      // nothing points at. Say so plainly rather than reporting a clean failure.
      const message = describeError(
        error,
        'The new file uploaded but the record still points at the old one.',
      );
      set({ status: '', error: message });
      updateUpload(key, { state: 'error', message });
    }
  };

  const hasSource = state.source === 'id' ? !!assetsFromVideoId(state.videoId) : !!state.file;
  const canReplace = hasSource && (!hasDerived || !!state.timing) && !state.status;

  return (
    <Box>
      <Button
        variant="outlined"
        startIcon={<SwapHorizIcon />}
        disabled={uploadBusy}
        onClick={() => setState({ ...EMPTY, open: true })}
      >
        Replace video file
      </Button>

      <Dialog
        open={state.open}
        onClose={state.status ? undefined : () => setState(EMPTY)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Replace video file</DialogTitle>
        <DialogContent dividers>
          {state.done ? (
            <Alert severity="success">
              <AlertTitle>Video replaced</AlertTitle>
              The record now points at the new file. api.video may take a few minutes to
              finish transcoding every quality level.
              {state.timing === 'changed' && (
                <Box sx={{ mt: 1 }}>
                  Its subtitles and chapters are flagged as out of date — regenerate them
                  from the Subtitles and Chapters tabs.
                </Box>
              )}
              {videoData.minithumbnailUrl && (
                <Box sx={{ mt: 1 }}>
                  The poster was regenerated from the new file, but the card crop still
                  comes from the old one — recrop it in the Artwork tab, or the home page
                  grid keeps showing the old video.
                </Box>
              )}
            </Alert>
          ) : (
            <>
              <DialogContentText component="div">
                This uploads a new file to api.video and repoints{' '}
                <strong>{videoIdentifier}</strong> at it.
              </DialogContentText>

              <Alert severity="warning" sx={{ mt: 2 }}>
                <AlertTitle>The current file is not deleted</AlertTitle>
                Replacing leaves the existing api.video asset
                {currentId ? <> (<code>{currentId}</code>)</> : null} in your account,
                where it keeps counting toward storage. Remove it in the api.video
                dashboard if you no longer need it.
              </Alert>

              <FormControl sx={{ mt: 2 }} disabled={!!state.status}>
                <FormLabel>Where is the new file?</FormLabel>
                <RadioGroup
                  row
                  value={state.source}
                  onChange={(e) => set({ source: e.target.value, error: '', probe: '' })}
                >
                  <FormControlLabel value="file" control={<Radio />} label="Upload it now" />
                  <FormControlLabel
                    value="id"
                    control={<Radio />}
                    label="Already on api.video"
                  />
                </RadioGroup>
              </FormControl>

              {state.source === 'file' ? (
                <Box sx={{ mt: 1 }}>
                  <Button variant="outlined" component="label" disabled={!!state.status}>
                    Choose file
                    <input
                      hidden
                      type="file"
                      accept="video/*"
                      aria-label="New video file"
                      onChange={(e) => set({ file: e.target.files[0] || null, error: '' })}
                    />
                  </Button>
                  {state.file && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {state.file.name} ({(state.file.size / 1024 / 1024).toFixed(1)} MB)
                    </Typography>
                  )}
                </Box>
              ) : (
                <Box sx={{ mt: 1 }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <TextField
                      size="small"
                      fullWidth
                      label="api.video id"
                      placeholder="vi4blUQJFrYWbaG44NChkH27"
                      value={state.videoId}
                      disabled={!!state.status}
                      onChange={(e) => set({ videoId: e.target.value, error: '', probe: '' })}
                    />
                    <Button
                      variant="outlined"
                      onClick={check}
                      disabled={!state.videoId.trim() || state.checking || !!state.status}
                      sx={{ flexShrink: 0 }}
                    >
                      {state.checking ? 'Checking…' : 'Check'}
                    </Button>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Upload the file in the api.video dashboard, then paste its id here. The
                    id, or any of the video's URLs, both work.
                  </Typography>
                  {state.probe && (
                    <Alert severity={PROBE_MESSAGE[state.probe][0]} sx={{ mt: 1 }}>
                      {PROBE_MESSAGE[state.probe][1]}
                    </Alert>
                  )}
                </Box>
              )}

              {hasDerived && (
                <FormControl sx={{ mt: 3 }} disabled={!!state.status}>
                  <FormLabel>
                    Does the new file have the same timeline as the current one?
                  </FormLabel>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                    This video has {subtitleCount} subtitle track{subtitleCount === 1 ? '' : 's'}
                    {chapterCount ? ` and ${chapterCount} chapter${chapterCount === 1 ? '' : 's'}` : ''},
                    all timed against the current file.
                  </Typography>
                  <RadioGroup value={state.timing} onChange={(e) => set({ timing: e.target.value })}>
                    <FormControlLabel
                      value="same"
                      control={<Radio />}
                      label="Same timeline — a re-encode, colour fix or quality bump. Keep them."
                    />
                    <FormControlLabel
                      value="changed"
                      control={<Radio />}
                      label="Timing changed — a recut or re-record. Flag them as out of date."
                    />
                  </RadioGroup>
                </FormControl>
              )}

              {state.status && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">{state.status}</Typography>
                  <LinearProgress
                    variant={state.progress ? 'determinate' : 'indeterminate'}
                    value={state.progress}
                    sx={{ mt: 1 }}
                  />
                </Box>
              )}

              {state.error && <Alert severity="error" sx={{ mt: 2 }}>{state.error}</Alert>}

              {currentId && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                  Current file:{' '}
                  <Link href={videoData.assets?.mp4} target="_blank" rel="noopener">
                    {currentId}
                  </Link>
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setState(EMPTY)} disabled={!!state.status}>
            {state.done ? 'Done' : 'Cancel'}
          </Button>
          {!state.done && (
            <Button variant="contained" color="warning" onClick={replace} disabled={!canReplace}>
              {state.status ? 'Working…' : 'Replace'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ReplaceVideoFile;
